import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';

/**
 * localStorage key persisting the "don't show again" choice for the
 * auto-detect-category warning (same pattern as APPLY_WARNING_DISMISS_KEY in
 * ContentMasterApplyWarningModal). When '1', checking the auto-detect box
 * enables it immediately without the consent modal.
 */
const AUTO_DETECT_WARNING_DISMISS_KEY = 'cm_auto_detect_warning_dismissed';

/** Reads the persisted preference. Safe on blocked storage (private mode / quota). */
export const isAutoDetectWarningDismissed = (): boolean => {
  try {
    return localStorage.getItem(AUTO_DETECT_WARNING_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/** Persists the preference; non-fatal when storage is unavailable. */
const setAutoDetectWarningDismissed = (): void => {
  try {
    localStorage.setItem(AUTO_DETECT_WARNING_DISMISS_KEY, '1');
  } catch {
    // The user will simply see the warning again next time.
  }
};

export interface AutoDetectCategoryWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The user consented - the caller enables the auto-detect flag. */
  onConfirm: () => void;
}

/**
 * Consent step shown when the "auto-detect category" checkbox is being turned
 * on: detected categories are assigned and written straight to Wawi (live
 * data). Shared by the bulk-generate and super-batch modals. The "don't show
 * again" choice is persisted here; callers check
 * `isAutoDetectWarningDismissed()` to skip the modal entirely.
 */
const AutoDetectCategoryWarningModal: React.FC<AutoDetectCategoryWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Fresh checkbox on every open.
  useEffect(() => {
    if (isOpen) {
      setDontShowAgain(false);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      hideCloseButton
      isCentered
      className="content-master-apply-warning-modal"
    >
      <div className="content-master-apply-warning-modal__inner">
        <div className="content-master-apply-warning-modal__icon" aria-hidden>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h3 className="content-master-apply-warning-modal__title">
          {t('marketplace_listing_auto_detect_warning_title')}
        </h3>

        <div className="content-master-apply-warning-modal__banner" role="alert">
          <p>{t('marketplace_listing_auto_detect_warning_body')}</p>
        </div>

        <label className="content-master-apply-warning-modal__checkbox">
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(event) => setDontShowAgain(event.target.checked)}
          />
          <span>{t('content_master_apply_warning_dont_show_again')}</span>
        </label>

        <div className="content-master-apply-warning-modal__buttons">
          <button
            type="button"
            className="content-master-apply-warning-modal__button content-master-apply-warning-modal__button--cancel"
            onClick={onClose}
          >
            {t('cancelText')}
          </button>
          <button
            type="button"
            className="content-master-apply-warning-modal__button content-master-apply-warning-modal__button--confirm"
            onClick={() => {
              if (dontShowAgain) {
                setAutoDetectWarningDismissed();
              }
              onConfirm();
            }}
          >
            {t('marketplace_listing_auto_detect_warning_confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AutoDetectCategoryWarningModal;
